import { Dialog, DialogContent } from '@quadminds/mindkit';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { useState } from 'react';
import { FileInformationPanel } from './components/FileInformationPanel';
import { FileModalHeader } from './components/FileModalHeader';
import { FilePermissionPanel } from './components/FilePermissionPanel';
import { FilePreviewPanel } from './components/FilePreviewPanel';
import { MobileTabNavigation } from './components/MobileTabNavigation';
import useMapPermissions from '../../../../../hooks/useMapPermissions';
import type { FileItem } from '../../../../../types/File/File.types';
import { hasAdminRole } from '../../../../../utils/utils';

interface FilePreviewModalProps {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly file: FileItem | null;
    readonly onAddComment: (fileId: string, comment: string) => void;
    readonly userInfo?: IAuthMeV2OutputDto;
}

export function FilePreviewModal({ isOpen, onClose, file, onAddComment: _, userInfo }: FilePreviewModalProps) {
    const isMindyAdmin = hasAdminRole(userInfo?.roles);
    const [activeTab, setActiveTab] = useState<string>('preview');
    const { isAdmin, isEditor } = useMapPermissions(file);

    if (!file) {
        return null;
    }

    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className={`max-w-[90vw] max-h-[90vh] overflow-hidden flex flex-col`}>
                <FileModalHeader file={file} />

                <div className="flex flex-col md:flex-row gap-4 overflow-hidden flex-1 py-4">
                    <div className={`overflow-hidden flex ${isAdmin || isEditor ? 'md:w-1/3' : 'md:w-1/2'}`}>
                        <FilePreviewPanel file={file} isVisible={activeTab === 'preview' || activeTab === 'all'} />
                    </div>
                    <div className={`overflow-hidden flex ${isAdmin || isEditor ? 'md:w-1/3' : 'md:w-1/2'}`}>
                        <FileInformationPanel file={file} isVisible={activeTab === 'info' || activeTab === 'all'} />
                    </div>
                    {(isAdmin || isEditor || isMindyAdmin) && (
                        <div className="overflow-hidden flex md:w-1/3">
                            <FilePermissionPanel
                                file={file}
                                isVisible={activeTab === 'permissions' || activeTab === 'all'}
                                onClose={onClose}
                                ownerAvatar={file.uploaded_by?.photo}
                                isOpen={isOpen}
                            />
                        </div>
                    )}
                </div>

                <MobileTabNavigation
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                    showPermissions={isAdmin || isEditor}
                />
            </DialogContent>
        </Dialog>
    );
}

export default FilePreviewModal;
